<template>
    <applicants-find
        v-slot="{
            data: applicant,
            error,
            is_loading,
            has_loaded,
            find
        }"
        :params="{
            applicant: $route.params.applicant
        }"
        :include="[
            'address',
            'application(include:listing(include:property))',
            'employers',
            'landlord',
            'tenant',
        ]"
    >
        <div>
            <template v-if="!has_loaded || error">
                <base-loading
                    :error="error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="error"
                    :error="error"
                />
            </template>

            <template v-else>
                <base-alert
                    v-if="!applicant.submitted_at"
                    level="warning"
                    class="mb-6"
                >
                    <template slot="heading">Co-Applicant Pending</template>

                    <template slot="content">
                        <p>This co-applicant has yet to complete their portion of the application. We'll notify you when it is completed. You may continue without it if you wish to do so.</p>
                    </template>
                </base-alert>

                <div class="mb-6 rounded shadow bg-grey-bg">
                    <header class="flex items-center px-4 bg-white border-b rounded-t h-18 sm:px-6">
                        <div class="flex-grow mr-3 overflow-hidden">
                            <h1 class="text-lg font-normal truncate">
                                {{ applicant.name }}
                            </h1>
                        </div>

                        <div class="flex-none">
                            <chats-store-button
                                :personas="_.filter([applicant.tenant])"
                            >
                                Message Applicant
                            </chats-store-button>
                        </div>
                    </header>

                    <div class="px-4 pt-3 sm:px-6 md:pt-4 md:pb-1">
                        <grid-layout>
                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs text-label">Application</div>
                                <div class="text-sm">
                                    <router-link
                                        :to="{
                                            name: 'dashboard.landlords.applications.show',
                                            params: {
                                                application: applicant.application.uuid
                                            }
                                        }"
                                        class="text-blue"
                                    >
                                        {{ applicant.application.listing.property.name }}
                                    </router-link>
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs text-label">Applied at</div>
                                <div class="text-sm">
                                    <template v-if="applicant.submitted_at">
                                        {{ applicant.submitted_at | moment('DD/MM/YYYY') }}
                                    </template>
                                    <base-na v-else />
                                </div>
                            </grid-layout-item>
                        </grid-layout>

                        <hr class="mb-3">

                        <grid-layout>
                            <grid-layout-item class="mb-3 lg:w-1/3">
                                <div class="text-xs text-label">Date of birth</div>
                                <div class="text-sm">
                                    <template v-if="applicant.date_of_birth">
                                        {{ applicant.date_of_birth | moment('DD/MM/YYYY') }}
                                    </template>
                                    <base-na v-else />
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mb-3 lg:w-2/3">
                                <div class="text-xs text-label">Address</div>
                                <div class="text-sm">
                                    <base-address
                                        v-if="applicant.address"
                                        :is-inline="true"
                                        :address="applicant.address"
                                    />
                                    <base-na v-else />
                                </div>
                            </grid-layout-item>
                        </grid-layout>
                    </div>
                </div>

                <div class="mb-6 bg-white rounded shadow">
                    <header class="flex items-center h-16 px-4 bg-white border-b rounded-t sm:px-6">
                        <h3 class="flex-grow text-base font-normal">Employers</h3>
                    </header>

                    <applicant-employers-list
                        v-if="applicant.employers.data.length"
                        :employers="applicant.employers.data"
                    />

                    <div
                        v-else
                        class="p-3 italic text-center text-grey bg-grey-bg"
                    >
                        There's nothing here!
                    </div>
                </div>

                <div class="mb-6 rounded shadow bg-grey-bg">
                    <header class="flex items-center h-16 px-4 bg-white border-b rounded-t sm:px-6">
                        <h3 class="flex-grow text-base font-normal">Landlord</h3>
                    </header>

                    <div
                        v-if="applicant.landlord && applicant.landlord.first_name"
                        class="px-4 pt-3 sm:px-6 md:pt-4 md:pb-1"
                    >
                        <grid-layout>
                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs text-label">Name</div>
                                <div class="text-sm">
                                    {{ applicant.landlord.first_name }} {{ applicant.landlord.last_name }}
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs text-label">Email</div>
                                <div class="text-sm">
                                    <a-mailto
                                        v-if="applicant.landlord.email"
                                        :email="applicant.landlord.email"
                                        class="text-blue"
                                    />
                                    <base-na v-else />
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs text-label">Telephone</div>
                                <div class="text-sm">
                                    <a-tel
                                        v-if="applicant.landlord.telephone"
                                        :telephone="applicant.landlord.telephone"
                                        class="text-blue"
                                    />
                                    <base-na v-else />
                                </div>
                            </grid-layout-item>
                        </grid-layout>
                    </div>

                    <div
                        v-else
                        class="p-3 italic text-center text-grey bg-grey-bg"
                    >
                        There's nothing here!
                    </div>
                </div>
            </template>
        </div>
    </applicants-find>
</template>

<script>
    import ApplicantEmployersList from '../../../../../components/applicant-employers-list'
    import ApplicantsFind from '../../../../../components/applicants-find'
    import ChatsStoreButton from '../../../../../components/chats-store-button'

    export default {
        components: {
            ApplicantEmployersList,
            ApplicantsFind,
            ChatsStoreButton
        }
    }
</script>
